import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useNotifications from '../../hooks/useNotifications';
import { useNotificationAlert } from '../../context/NotificationAlertContext';
import NotificationPanel from '../notifications/NotificationPanel';
import NotificationAlert from '../notifications/NotificationAlert';
import './AdminLayout.css';

const sidebarVariants = {
  hidden: { x: -280, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 220, damping: 26 } }
};

const dropdownVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 380, damping: 28 } },
  exit: { opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.15 } }
};

const AdminLayout = ({ children }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [user, setUser]           = useState(null);
  const [showPanel, setShowPanel]     = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef(null);

  const { notifications, unreadCount, markRead, markAllRead, remove } = useNotifications();
  const { triggerAlert } = useNotificationAlert();

  // Ferme le dropdown profil si clic en dehors
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const getInitials = () => {
    if (!user) return 'A';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || 'A'}`.toUpperCase();
  };

  const handleNotifClick = (notif) => {
    triggerAlert(notif.title);
    if (notif.target_url) {
      navigate(notif.target_url, {
        state: notif.entity_type && notif.entity_id
          ? { highlightEntity: { type: notif.entity_type, id: notif.entity_id } }
          : undefined,
      });
    }
  };

  const menuItems = [
    { path: '/dashboard/admin',              icon: 'fa-chart-pie',    label: 'Dashboard'    },
    { path: '/dashboard/admin/partners',     icon: 'fa-users',        label: 'Partenaires'  },
    { path: '/dashboard/admin/clients',      icon: 'fa-user',         label: 'Clients'      },
    { path: '/dashboard/admin/packs',        icon: 'fa-cube',         label: 'Packs B2B'    },
    { path: '/dashboard/admin/transactions', icon: 'fa-credit-card',         label: 'Transactions' },
    { path: '/dashboard/admin/invoices',    icon: 'fa-file-invoice-dollar',  label: 'Factures'     },
    { path: '/dashboard/admin/currencies',   icon: 'fa-exchange-alt', label: 'Devises'      },
    { path: '/dashboard/admin/security',     icon: 'fa-shield-alt',   label: 'Sécurité'     },
  ];

  const pageTitles = {
    '/dashboard/admin':              'Dashboard',
    '/dashboard/admin/partners':     'Partenaires',
    '/dashboard/admin/clients':      'Clients',
    '/dashboard/admin/packs':        'Packs B2B',
    '/dashboard/admin/transactions': 'Transactions',
    '/dashboard/admin/invoices':     'Factures',
    '/dashboard/admin/currencies':   'Devises',
    '/dashboard/admin/security':     'Sécurité',
  };

  const pageSubtitles = {
    '/dashboard/admin':              "Vue d'ensemble de la plateforme",
    '/dashboard/admin/partners':     'Validation et gestion des partenaires',
    '/dashboard/admin/clients':      'Gestion des clients',
    '/dashboard/admin/packs':        'Gestion des packs B2B',
    '/dashboard/admin/transactions': 'Historique des transactions',
    '/dashboard/admin/invoices':     'Factures soumises par les partenaires',
    '/dashboard/admin/currencies':   'Gestion des taux de change',
    '/dashboard/admin/security':     "Gouvernance, contrôle d'accès et traçabilité",
  };

  const currentTitle    = pageTitles[location.pathname]    || 'Admin';
  const currentSubtitle = pageSubtitles[location.pathname] || '';

  return (
    <div className="admin-app">
      <motion.aside className="admin-sidebar" variants={sidebarVariants} initial="hidden" animate="visible">
        <div className="admin-sidebar-header">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <div className="logo-text">Proxy<span>Runit</span>Pay</div>
          </div>
        </div>

        <div className="admin-info-block">
          <div className="admin-avatar-sm">{getInitials()}</div>
          <div className="admin-details">
            <h4>{user?.first_name || 'Admin'} {user?.last_name || ''}</h4>
            <p>Super Admin <span className="badge-2fa-sm">2FA</span></p>
          </div>
        </div>

        <nav className="admin-nav-menu">
          {menuItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <motion.div
                key={item.path}
                className={`admin-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.96 }}
              >
                <i className={`fas ${item.icon}`}></i>
                <span>{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="adminActiveIndicator"
                    style={{ position: 'absolute', right: 8, width: 6, height: 6, backgroundColor: '#3b82f6', borderRadius: '50%' }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  />
                )}
              </motion.div>
            );
          })}
        </nav>

        <div className="admin-nav-footer">
          <motion.div
            className="admin-nav-item logout-btn"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('userType');
              navigate('/login/admin');
            }}
            whileHover={{ x: 3 }}
            whileTap={{ scale: 0.97 }}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Déconnexion</span>
          </motion.div>
        </div>
      </motion.aside>

      <main className="admin-main">
        <motion.header
          className="admin-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 240, damping: 24 }}
        >
          <div className="admin-header-left">
            <h1 className="admin-page-title">{currentTitle}</h1>
            <p className="admin-page-subtitle">{currentSubtitle}</p>
          </div>

          <div className="admin-header-actions">
            {/* Cloche notifications */}
            <motion.button
              className={`admin-icon-btn${showPanel ? ' active' : ''}`}
              onClick={() => { setShowPanel(true); setShowProfile(false); }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.93 }}
            >
              <i className="far fa-bell"></i>
              {unreadCount > 0 && (
                <motion.span
                  className="admin-notif-badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </motion.span>
              )}
            </motion.button>

            {/* Profile */}
            <div className="admin-dropdown-wrap" ref={profileRef}>
              <motion.button
                className={`admin-user-menu${showProfile ? ' active' : ''}`}
                onClick={() => { setShowProfile(v => !v); }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <div className="admin-avatar-btn">{getInitials()}</div>
                <div className="admin-user-info">
                  <span className="admin-user-name">{user?.first_name || 'Admin'} {user?.last_name || ''}</span>
                  <span className="admin-user-role">Super Admin</span>
                </div>
                <motion.i className="fas fa-chevron-down" animate={{ rotate: showProfile ? 180 : 0 }} transition={{ duration: 0.2 }} />
              </motion.button>

              <AnimatePresence>
                {showProfile && (
                  <motion.div className="admin-dropdown admin-profile-panel" variants={dropdownVariants} initial="hidden" animate="visible" exit="exit">
                    <div className="admin-profile-header">
                      <div className="admin-avatar-lg">{getInitials()}</div>
                      <div>
                        <div className="admin-profile-name">{user?.first_name} {user?.last_name}</div>
                        <div className="admin-profile-email">{user?.email}</div>
                        <span className="admin-profile-badge">Super Admin</span>
                      </div>
                    </div>
                    <div className="admin-dropdown-divider" />
                    <button className="admin-dropdown-item danger" onClick={() => {
                      localStorage.removeItem('token');
                      localStorage.removeItem('user');
                      localStorage.removeItem('userType');
                      navigate('/login/admin');
                    }}>
                      <i className="fas fa-sign-out-alt"></i> Déconnexion
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.header>

        {/* Alerte flash après clic sur une notification */}
        <NotificationAlert />

        <div className="admin-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>

        <footer className="admin-footer">
          <div>© 2026 ProxyRunitPay — Administration</div>
          <div className="admin-footer-links">
            <span>Dernier audit: 15/02/2026 11:30</span>
          </div>
        </footer>
      </main>

      {/* Panneau notifications */}
      <NotificationPanel
        open={showPanel}
        onClose={() => setShowPanel(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onRemove={remove}
        onNotifClick={handleNotifClick}
      />
    </div>
  );
};

export default AdminLayout;
